import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../services/accounts/auth.service';
import { RbacService } from '../services/accounts/rbac.service';

/**
 * Blocks direct navigation to a route whose organisation-level module(s) are disabled, or
 * whose required permission(s) the user doesn't hold — even if their role would otherwise
 * allow it. Configure with route data, e.g. `data: { module: ['admin', 'users'], permission: 'users.user.view' }`
 * (a single string is accepted for either; permission is optional).
 */
@Injectable({
  providedIn: 'root'
})
export class ModuleGuard implements CanActivate {

  constructor(private auth: AuthService, private rbac: RbacService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    const requiredModules = this.toArray(route.data?.['module']);
    const requiredPermissions = this.toArray(route.data?.['permission']);
    if (!requiredModules.length && !requiredPermissions.length) {
      return true;
    }

    const cached = this.rbac.getSnapshot();
    if (cached) {
      return this.evaluate(requiredModules, requiredPermissions);
    }

    if (!this.auth.requireAuthenticated()) {
      return false;
    }

    return this.rbac.fetchMe().pipe(
      map(() => this.evaluate(requiredModules, requiredPermissions)),
      catchError((error) => {
        this.auth.handleRbacFetchError(error);
        return of(false);
      })
    );
  }

  private toArray(value: string | string[] | undefined): string[] {
    if (!value) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  }

  private evaluate(requiredModules: string[], requiredPermissions: string[]): boolean {
    const allowed = this.rbac.hasAllModules(requiredModules) && this.rbac.hasAllPermissions(requiredPermissions);
    if (!allowed) {
      this.router.navigate(['/booking']);
    }
    return allowed;
  }
}
